import Service, { service } from '@ember/service'
import { isNone } from '@ember/utils'
import { tracked } from '@glimmer/tracking'
import type SubscriptionUtilisation from 'teacher-ui-client/models/subscription-utilisation'
import type SubscriptionTypeRoute from 'teacher-ui-client/subscription-type/route'
import type SessionService from './session'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type RouterService from '@ember/routing/router-service'
import type PartnerService from './partner'
import { PartnerCode } from 'teacher-ui-client/partner/partners'

export default class SideNavService extends Service {
  @service partner!: PartnerService
  @service router!: RouterService
  @service session!: SessionService
  @tracked currentSubscriptionType?: SubscriptionType
  @tracked subscriptionUtilisation?: SubscriptionUtilisation

  get countryCode() {
    return this.session.currentSchool.countryCode
  }
  get currentTeacher() {
    return this.session.currentTeacher
  }

  get hasRatnasagarPartner() {
    if (!this.currentSubscriptionType) return false
    return this.partner.hasPartner(PartnerCode.RatnaSagar, this.currentSubscriptionType)
  }

  get hasTrialSub() {
    if (!this.currentSubscriptionType) return false
    const schoolSubscriptions = this.currentTeacher.school.schoolSubscriptions
    const hasActiveSub = schoolSubscriptions.find((schoolSub) => {
      return schoolSub.subscriptionType === this.currentSubscriptionType && !schoolSub.isExpired
    })

    const hasTrialSub = this.currentTeacher.hasTrialSubscriptionFor(this.currentSubscriptionType)
    return hasTrialSub && !hasActiveSub
  }

  get hasUiScope() {
    return this.router.currentRouteName?.includes('ui-scope')
  }

  get isHomeschool() {
    return this.session.currentSchool.isHomeschool
  }

  get isSubco() {
    if (!this.currentSubscriptionType) return false
    return this.session.currentTeacher.subcoFor(this.currentSubscriptionType)
  }

  get isSchoolExternallyManaged() {
    return (
      this.session.currentSchool.usesExternalAccountProvisioning ||
      this.session.currentSchool.usesExternalRostering ||
      this.session.currentSchool.usesExternalAuthentication
    )
  }

  get limitedAccess() {
    return isNone(this.subscriptionUtilisation)
  }

  get showCatchUpGuides() {
    return this.countryCode === 'US' || this.countryCode === 'CA'
  }

  get showHelpJuiceBanner() {
    return !this.isHomeschool && this.countryCode === 'US'
  }

  get showPrintables() {
    return !(this.hasRatnasagarPartner && this.hasTrialSub)
  }

  // Hides the teacher toolkit and printables library links if
  // the school has a partner of Ratnasagar and
  // the school does not have an active subscription and
  // the teacher is on a trial subscription
  get showTeacherToolkit() {
    return !(this.hasRatnasagarPartner && this.hasTrialSub)
  }

  /**
   * If page that the user is on already has access to ui-scope dynamic segments, don't need to provide them, LinkTo
   * will infer them.
   * Otherwise, provide default ones to allow access to the reporting area.
   */
  get uiScopeModels() {
    if (this.hasUiScope) return []
    return this.isSubco ? ['school', this.session.currentSchool.id] : ['teacher', this.session.currentTeacher.id]
  }

  updateState(model: ModelFor<SubscriptionTypeRoute>) {
    this.currentSubscriptionType = model.subscriptionType
    this.subscriptionUtilisation = model.subscriptionUtilisation
  }
}

declare module '@ember/service' {
  interface Registry {
    'side-nav': SideNavService
  }
}
